import LogRocket from "logrocket";
import setUpReact from "logrocket-react";
import ReactGA from "react-ga4";
import { isProd } from "../application";

// Shared key for cross-app coordination
const LOGROCKET_INIT_KEY = 'villa_logrocket_initialized';
const GA_INIT_KEY = 'villa_ga_initialized';

// Check if LogRocket is already initialized by any app
const isLogRocketInitialized = (): boolean => {
    // Check localStorage first
    if (localStorage.getItem(LOGROCKET_INIT_KEY) === 'true') {
        return true;
    }

    // Check if LogRocket is already available on window
    if (typeof window !== 'undefined' && (window as any).LogRocket) {
        // Check if LogRocket has been properly initialized by checking for session
        try {
            const sessionURL = LogRocket.sessionURL;
            if (sessionURL) {
                localStorage.setItem(LOGROCKET_INIT_KEY, 'true');
                return true;
            }
        } catch (e) {
            // LogRocket not properly initialized
        }
    }

    return false;
};

// Check if Google Analytics is already initialized by any app
const isGoogleAnalyticsInitialized = (): boolean => {
    // Check localStorage first
    if (localStorage.getItem(GA_INIT_KEY) === 'true') {
        return true;
    }

    // Check if gtag is available
    if (typeof window !== 'undefined' && (window as any).gtag) {
        localStorage.setItem(GA_INIT_KEY, 'true');
        return true;
    }

    return false;
};

export interface AnalyticsConfig {
    logRocketProjectId?: string;
    googleAnalyticsId?: string;
    enableReactIntegration?: boolean;
}

export const SharedAnalyticsManager = {
    /**
     * Initialize analytics with coordination across apps
     */
    initialize: (config: AnalyticsConfig = {}) => {
        if (!analyticsAllowed()) {
            return {
                logRocketInitialized: false,
                googleAnalyticsInitialized: false,
                alreadyInitialized: false
            };
        }

        const {
            logRocketProjectId = "dvu2wa/natomas",
            googleAnalyticsId = "G-PZHCFP48KJ",
            enableReactIntegration = true
        } = config;

        let logRocketWasInitialized = false;
        let googleAnalyticsWasInitialized = false;
        let alreadyInitialized = false;

        // Initialize LogRocket if not already done
        if (!isLogRocketInitialized()) {
            try {
                LogRocket.init(logRocketProjectId, {
                    rootHostname: "villahomes.com",
                    shouldCaptureIP: false,
                    dom: {
                        // Don't record form inputs
                        inputSanitizer: true,
                    },
                    network: {
                        requestSanitizer: (request) => {
                            if (request.body && request.body.includes("password")) {
                                // don't log passwords
                                request.body = undefined;
                                return request;
                            }
                            // otherwise log the request normally
                            return request;
                        },
                    },
                });

                if (enableReactIntegration) {
                    setUpReact(LogRocket);
                }

                localStorage.setItem(LOGROCKET_INIT_KEY, 'true');
                logRocketWasInitialized = true;

                console.log('LogRocket initialized by villa-app');
            } catch (error) {
                console.error('Failed to initialize LogRocket:', error);
            }
        } else {
            alreadyInitialized = true;
            console.log('LogRocket already initialized, skipping');
        }

        // Initialize Google Analytics if not already done
        if (!isGoogleAnalyticsInitialized()) {
            try {
                ReactGA.initialize(googleAnalyticsId);
                localStorage.setItem(GA_INIT_KEY, 'true');
                googleAnalyticsWasInitialized = true;

                console.log('Google Analytics initialized by villa-app');
            } catch (error) {
                console.error('Failed to initialize Google Analytics:', error);
            }
        } else {
            console.log('Google Analytics already initialized, skipping');
        }

        return {
            logRocketInitialized: logRocketWasInitialized,
            googleAnalyticsInitialized: googleAnalyticsWasInitialized,
            alreadyInitialized
        };
    },

    /**
     * Identify user in LogRocket (safe to call multiple times)
     */
    identifyUser: (userId: string, traits: Record<string, any> = {}) => {
        if (!analyticsAllowed()) {
            return;
        }

        try {
            // Check if LogRocket is available (either initialized by this app or another)
            if (typeof LogRocket !== 'undefined' && LogRocket.identify) {
                LogRocket.identify(userId, traits);
            }
        } catch (error) {
            console.error('Failed to identify user in LogRocket:', error);
        }
    },

    /**
     * Clear all analytics data and reset initialization flags
     */
    reset: () => {
        try {
            // Clear localStorage flags
            localStorage.removeItem(LOGROCKET_INIT_KEY);
            localStorage.removeItem(GA_INIT_KEY);

            // Reset LogRocket
            if (typeof LogRocket !== 'undefined' && LogRocket.identify) {
                LogRocket.identify({ anonymous: true });
            }

            // Reset Google Analytics
            if (typeof ReactGA !== 'undefined') {
                ReactGA.set({ anonymizeIp: true });
                ReactGA.reset();
            }
        } catch (error) {
            console.error('Failed to reset analytics:', error);
        }
    },

    /**
     * Check if analytics are currently initialized
     */
    isInitialized: () => ({
        logRocket: isLogRocketInitialized(),
        googleAnalytics: isGoogleAnalyticsInitialized()
    }),

    /**
     * Manually mark as initialized (for external initialization)
     */
    markAsInitialized: (service: 'logRocket' | 'googleAnalytics' | 'both') => {
        if (service === 'logRocket' || service === 'both') {
            localStorage.setItem(LOGROCKET_INIT_KEY, 'true');
        }
        if (service === 'googleAnalytics' || service === 'both') {
            localStorage.setItem(GA_INIT_KEY, 'true');
        }
    }
};

const analyticsAllowed = () => {
    return isProd;
}; 